import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import PortalRegisterModal from "../components/PortalRegisterModal";
import SiteHeader from "../components/SiteHeader";
import { getPastLabs, getUpcomingLabs } from "../firebase";
import { formatLabDate } from "../utils/datetime";
import { buildResourceSummary } from "../utils/resources";

const RESOURCES_SOURCE = {
  sourceType: "resources",
  sourceSlug: "school-leader-ai-lab-resources",
  sourceName: "School Leader AI Lab Resources",
};

const RESOURCES_SECTIONS = [
  {
    id: "resources-all",
    label: "1) All resources",
    helper: "No duplicates",
  },
  {
    id: "resources-by-session",
    label: "2) Resources by session",
    helper: "Session context",
  },
];

const resourceMatchesQuery = (resource, queryValue) => {
  if (!queryValue) {
    return true;
  }

  const query = queryValue.toLowerCase();
  return (
    resource.label.toLowerCase().includes(query) ||
    resource.url.toLowerCase().includes(query)
  );
};

function ResourcesPage() {
  const [upcomingLabs, setUpcomingLabs] = useState([]);
  const [pastLabs, setPastLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(RESOURCES_SECTIONS[0].id);

  useEffect(() => {
    let isActive = true;

    const loadLabs = async () => {
      setIsLoading(true);
      setError("");

      try {
        const [upcomingResponse, pastResponse] = await Promise.all([
          getUpcomingLabs(12),
          getPastLabs(100),
        ]);

        if (isActive) {
          setUpcomingLabs(upcomingResponse);
          setPastLabs(pastResponse);
        }
      } catch (loadError) {
        console.error("Failed to load resources page data:", loadError);
        if (isActive) {
          setError(
            "We couldn't load resources right now. Please refresh in a moment."
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLabs();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const sectionElements = RESOURCES_SECTIONS.map((section) =>
      document.getElementById(section.id)
    ).filter(Boolean);

    if (sectionElements.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((left, right) => right.intersectionRatio - left.intersectionRatio);

        if (visibleEntries.length > 0) {
          setActiveSectionId(visibleEntries[0].target.id);
        }
      },
      {
        rootMargin: "-35% 0px -45% 0px",
        threshold: [0.2, 0.4, 0.6],
      }
    );

    sectionElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const allLabs = useMemo(() => [...upcomingLabs, ...pastLabs], [upcomingLabs, pastLabs]);
  const resourceSummary = useMemo(() => buildResourceSummary(allLabs), [allLabs]);
  const filteredDedupedResources = useMemo(
    () =>
      resourceSummary.dedupedResources.filter((resource) =>
        resourceMatchesQuery(resource, searchValue.trim())
      ),
    [resourceSummary, searchValue]
  );
  const nextLab = upcomingLabs[0] || null;

  return (
    <>
      <SiteHeader
        registerLabel="Not registered yet? Register here"
        onRegisterClick={() => setIsRegisterOpen(true)}
      />

      <main className="portal-main">
        <section className="hero section portal-hero">
          <div className="container">
            <h1>All AI Lab Resources</h1>
            <p className="hero-copy portal-hero-copy">
              Every published resource in one place, plus a by-session view for
              full context.
            </p>

            <div className="portal-nav-shell">
              <p className="portal-nav-heading">Jump to what you need</p>
              <nav className="portal-section-nav" aria-label="Resources sections">
                {RESOURCES_SECTIONS.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`portal-nav-btn ${
                      activeSectionId === section.id ? "active" : ""
                    }`}
                    aria-current={activeSectionId === section.id ? "location" : undefined}
                  >
                    <span className="portal-nav-hint">{section.helper}</span>
                    <span className="portal-nav-label">{section.label}</span>
                  </a>
                ))}
                <Link className="portal-nav-btn portal-nav-back" to="/hub">
                  <span className="portal-nav-hint">Go back</span>
                  <span className="portal-nav-label">Back to hub</span>
                </Link>
              </nav>
            </div>
          </div>
        </section>

        {error ? (
          <section className="section">
            <div className="container">
              <p className="status-message error">{error}</p>
            </div>
          </section>
        ) : null}

        <section id="resources-all" className="section portal-section-target">
          <div className="container">
            <div className="portal-resources-header">
              <div>
                <h2>All resources (no duplicates)</h2>
                <p className="muted-copy">
                  Search all published resources across every lab.
                </p>
              </div>
              <label className="form-field portal-resource-search" htmlFor="resourceSearch">
                <span>Search resources</span>
                <input
                  id="resourceSearch"
                  type="search"
                  placeholder="Search by resource name or URL"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                />
              </label>
            </div>

            {isLoading ? <p>Loading resources...</p> : null}

            {!isLoading && filteredDedupedResources.length === 0 ? (
              <p>No resources match your search.</p>
            ) : (
              <div className="portal-resource-grid">
                {filteredDedupedResources.map((resource) => (
                  <article key={resource.id} className="card portal-resource-card">
                    <h3>{resource.label}</h3>
                    <p className="muted-copy">
                      Used in {resource.usageCount}{" "}
                      {resource.usageCount === 1 ? "session" : "sessions"}
                    </p>
                    <a href={resource.url} target="_blank" rel="noreferrer">
                      Open resource
                    </a>

                    <p className="portal-resource-meta">
                      Recently used in{" "}
                      <Link
                        to={`/lab/${resource.sessions[0].labId}`}
                        state={{ hubMode: true, backTo: "/resources" }}
                      >
                        {resource.sessions[0].labTitle}
                      </Link>
                    </p>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section
          id="resources-by-session"
          className="section section-alt portal-section-target"
        >
          <div className="container">
            <h2>Resources by session</h2>
            <p className="muted-copy">
              View resources in the context of the session where they were used.
            </p>

            {isLoading ? <p>Loading sessions...</p> : null}

            {!isLoading && resourceSummary.labsWithResources.length === 0 ? (
              <p>No resources are published yet.</p>
            ) : (
              <div className="portal-labs-grid">
                {resourceSummary.labsWithResources.map((lab) => (
                  <article key={lab.id} className="card portal-lab-card">
                    <p className="eyebrow">{formatLabDate(lab.dateTime)}</p>
                    <h3>{lab.title}</h3>

                    <ul className="resource-list">
                      {lab.resources.map((resource) => (
                        <li key={`${lab.id}-${resource.label}-${resource.url}`}>
                          <a href={resource.url} target="_blank" rel="noreferrer">
                            {resource.label}
                          </a>
                        </li>
                      ))}
                    </ul>

                    <div className="action-row portal-action-row">
                      <Link
                        className="btn btn-primary"
                        to={`/lab/${lab.id}`}
                        state={{ hubMode: true, backTo: "/resources" }}
                      >
                        View details
                      </Link>
                      {lab.youtubeId ? (
                        <a
                          className="btn btn-outline"
                          href={`https://www.youtube.com/watch?v=${lab.youtubeId}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Watch recording
                        </a>
                      ) : (
                        <span className="portal-disabled-action">Recording coming soon</span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <PortalRegisterModal
        isOpen={isRegisterOpen}
        onClose={() => setIsRegisterOpen(false)}
        nextLab={nextLab}
        source={RESOURCES_SOURCE}
        submitLabel="Register free in under 30 seconds"
      />

      <Footer />
    </>
  );
}

export default ResourcesPage;
